import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { loadGoogleMaps } from "@/lib/googleMaps";
import { renderSiteMarkers, siteInfoHtml } from "@/lib/siteMarkers";
import { ExternalLink, MapPin } from "lucide-react";

function statusColor(p) {
  if (p.ignition && p.speed > 3) return "#10B981";
  if (p.ignition) return "#F59E0B";
  return "#94A3B8";
}

export default function DashboardFleetMap({ sites = [], height = 460 }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const mapsRef = useRef(null);
  const siteMarkersRef = useRef([]);
  const infoRef = useRef(null);
  const [count, setCount] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(async (maps) => {
      if (cancelled || !mapRef.current) return;
      mapsRef.current = maps;
      mapObj.current = new maps.Map(mapRef.current, {
        center: { lat: 45.34, lng: 9.35 }, zoom: 9,
        disableDefaultUI: true, zoomControl: true, streetViewControl: false,
      });
      infoRef.current = new maps.InfoWindow();
      setReady(true);
      try {
        const { data } = await api.get("/equipment/live-positions");
        if (cancelled) return;
        setCount(data.count);
        const bounds = new maps.LatLngBounds();
        (data.positions || []).forEach(pnt => {
          if (!pnt.lat || !pnt.lon) return;
          const pos = { lat: pnt.lat, lng: pnt.lon };
          bounds.extend(pos);
          new maps.Marker({
            // Qui i mezzi non hanno il fumetto, solo il titolo che compare
            // passandoci sopra: ci si mette anche l'autista.
            position: pos, map: mapObj.current,
            title: pnt.driver_name ? `${pnt.identifier} · ${pnt.driver_name}` : pnt.identifier,
            icon: { path: maps.SymbolPath.CIRCLE, scale: 6, fillColor: statusColor(pnt), fillOpacity: 1, strokeColor: "#fff", strokeWeight: 1.5 },
          });
        });
        if (!bounds.isEmpty()) mapObj.current.fitBounds(bounds);
      } catch { /* silenzioso: la card resta comunque utile senza mezzi */ }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Bandierine dei cantieri del giorno, ridisegnate quando cambia l'elenco.
  useEffect(() => {
    let cancelled = false;
    const maps = mapsRef.current;
    if (!maps || !mapObj.current) return;
    siteMarkersRef.current.forEach(m => m.setMap(null));
    siteMarkersRef.current = [];
    renderSiteMarkers(maps, mapObj.current, sites, {
      onClick: (site, marker) => {
        if (!infoRef.current) return;
        infoRef.current.setContent(siteInfoHtml(site));
        infoRef.current.open(mapObj.current, marker);
      },
    }).then(markers => {
      if (cancelled) { markers.forEach(m => m.setMap(null)); return; }
      siteMarkersRef.current = markers;
    });
    return () => { cancelled = true; };
  }, [sites, ready]);

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 relative">
      <div ref={mapRef} style={{ height, width: "100%" }} />
      <div className="absolute bottom-2 right-2 flex items-center gap-2">
        {sites.length > 0 && (
          <span className="bg-white shadow rounded px-2.5 py-1.5 text-xs font-medium text-[#0067b2] flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {sites.length} cantieri oggi
          </span>
        )}
        <Link to="/localizzazione"
              className="bg-white shadow rounded px-2.5 py-1.5 text-xs font-medium text-orange-600 flex items-center gap-1 hover:bg-orange-50">
          {count != null ? `${count} mezzi` : "Vedi mappa"} <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
